import { createClient } from '@supabase/supabase-js'

// =============================================================================
// SUPABASE.JS — Supabase client, auth, and all data-access functions
// =============================================================================
// SECTIONS:
//   [CLIENT]     Supabase client init + exported URL/key
//   [ACTIVITY]   Activity log (fire-and-forget audit trail)
//   [AUTH]       Authentication (signIn, signOut, getSession)
//   [DEALS]      Deal CRUD + bulk import
//   [PROFILES]   User profile + agent management
//   [REGISTRATION] New org signup flow
//   [FILES]      Storage upload + signed URLs
//   [CREATORS]   Creator CRUD
//   [BRAND_NOTES] CRM brand notes
//   [LEADS]      Personal CRM leads
//   [PAYOUTS]    Commission payout tracking
//   [NOTIFICATIONS] In-app notifications
// =============================================================================

// --- [CLIENT] Supabase client ---
export const SUPABASE_URL = 'https://ykfyowmnmljbvbefsjou.supabase.co'
export const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlrZnlvd21ubWxqYnZiZWZzam91Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ1NjI5OTksImV4cCI6MjA5MDEzODk5OX0._sh9eCIBrugODNmxhriTFdd1OhWnQt2t2MeSXp5r_7s'
// Legacy aliases (referenced internally by supabase helper functions)
const supabaseUrl = SUPABASE_URL
const supabaseAnonKey = SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// --- ACTIVITY LOG (fire-and-forget, never blocks the UI) -----------------------
let _currentUser = null
export function setActivityUser(user) { _currentUser = user }

function _log(action, entityType, entityLabel, entityId, details) {
  if (!_currentUser) return
  supabase.from('activity_log').insert([{
    user_id: _currentUser.id,
    user_name: _currentUser.full_name || _currentUser.agent_name || _currentUser.email || 'Unknown',
    action, entity_type: entityType,
    entity_id: entityId || null,
    entity_label: entityLabel || null,
    details: details || {},
    org_id: _currentUser.org_id || null
  }]).then(() => {}) // fire-and-forget
}

// Run a write and record it in the activity log ONLY if it actually happened.
//
// Several callers below used to _log() first and then fire the query, so the
// feed recorded deletions that failed and edits that row-level security
// rejected. Worse, RLS does not raise on a filtered write: it returns
// error:null with zero rows, which is indistinguishable from success unless the
// returned rows are counted. Deletes therefore ask for .select() so there is
// something to count.
async function _loggedWrite(run, log) {
  const result = await run()
  const rows = result?.data
  const wrote = !result?.error && (rows == null || !Array.isArray(rows) || rows.length > 0)
  if (wrote) log(Array.isArray(rows) ? rows[0] : rows)
  return result
}


// =============================================================================
// PAGED READS — why these are not plain .select()
// =============================================================================
// PostgREST caps a response at 1,000 rows by default. A capped response is not
// an error: it comes back 200 OK with exactly 1,000 rows, so a list that has
// outgrown the cap renders as a complete list and every total computed from it
// is quietly wrong. activity_log passed 3,000 rows some time ago; leads and
// deals are in the 400s and climbing.
//
// Nothing in the app is big enough yet to need real server-side pagination with
// server-computed totals, and retrofitting that would mean rewriting every
// filter and every total on every screen. So this does the smaller, honest
// thing: page until the table is exhausted, and report the count alongside the
// rows so a caller can tell truncation from "that is all of them".
const PAGE_SIZE = 1000

/**
 * Read every row of a query, a page at a time.
 *
 * @param {Function} build  () => a fresh PostgREST query builder. Called once
 *                          per page — a builder cannot be reused after await.
 * @param {number}   maxRows Safety stop so a runaway table cannot hang the tab.
 * @returns {{data, error, count, truncated}}
 */
export async function fetchAllPages(build, maxRows = 20000) {
  const rows = []
  let from = 0
  let total = null

  for (;;) {
    const to = Math.min(from + PAGE_SIZE, maxRows) - 1
    if (to < from) break
    const { data, error, count } = await build().range(from, to)
    if (error) return { data: null, error, count: total, truncated: false }
    if (count !== null && count !== undefined) total = count
    const page = data || []
    rows.push(...page)
    if (page.length < to - from + 1) break   // last page
    from = to + 1
    if (from >= maxRows) {
      // Hit our own ceiling rather than the end of the table. Say so.
      return { data: rows, error: null, count: total, truncated: true }
    }
  }

  return {
    data: rows,
    error: null,
    count: total,
    truncated: total !== null && rows.length < total,
  }
}

export async function fetchActivityLog(limit = 100) {
  // Deliberately windowed, not paged: nobody scrolls 3,000 log lines. The exact
  // count comes back so the screen can state the window rather than presenting
  // the newest 100 rows as the whole history.
  const { data, error, count } = await supabase
    .from('activity_log')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .limit(limit)
  return { data, error, count, truncated: (count ?? 0) > (data?.length ?? 0) }
}

// Team activity for the analytics tab — last 30 days of actions.
// Returns rich rows so the client can roll up by user/day/entity_type.
// Time in app (see lib/appTime.js). Writes only through track_app_time, which
// credits real elapsed time; admins read the whole org, others only themselves.
export async function trackAppTime(seconds) {
  return supabase.rpc('track_app_time', { p_seconds: seconds })
}
export async function fetchAppTime(sinceDay) {
  return supabase.from('app_time_daily').select('user_id, day, seconds, last_seen_at').gte('day', sinceDay)
}

export async function fetchTeamActivity(days = 30) {
  const sinceISO = new Date(Date.now() - days * 86400000).toISOString()
  return supabase
    .from('activity_log')
    .select('user_id, user_name, action, entity_type, entity_label, created_at')
    .gte('created_at', sinceISO)
    .order('created_at', { ascending: false })
    .limit(5000)
}

// --- AUTH ----------------------------------------------------------------------
export async function signIn(email, password) {
  return supabase.auth.signInWithPassword({ email, password })
}
export async function signOut() { await supabase.auth.signOut() }

/**
 * Whether the signed-in session still owes a second factor.
 *
 * signInWithPassword() establishes a session immediately, even for an account
 * with TOTP enrolled — it just sits at assurance level aal1. The app used to
 * treat "has a session" as "is authenticated", so the dashboard mounted the
 * moment the password was accepted and the MFA screen behind it never
 * mattered. Supabase's own answer is to compare the levels: when nextLevel is
 * aal2 and currentLevel is not, verification has not happened yet.
 *
 * NOTE: this is the client-side gate. It stops the UI rendering, which is what
 * the user sees, but it is not a security boundary on its own — a determined
 * caller still holds an aal1 token. Policies that must hold the line need to
 * check the token's aal claim server-side.
 */
export async function getMfaGate() {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
  if (error) return { required: false, currentLevel: null, nextLevel: null, error }
  const currentLevel = data?.currentLevel ?? null
  const nextLevel = data?.nextLevel ?? null
  return {
    required: nextLevel === 'aal2' && currentLevel !== 'aal2',
    currentLevel,
    nextLevel,
    error: null,
  }
}
export async function getSession() { const { data } = await supabase.auth.getSession(); return data.session }

// --- DEALS ---------------------------------------------------------------------
export async function fetchDeals() {
  return fetchAllPages(() =>
    supabase.from('deals').select('*', { count: 'exact' }).order('date', { ascending: false }))
}
export async function createDeal(deal) {
  const result = await supabase.from('deals').insert([deal]).select()
  if (result.data?.[0]) _log('create', 'deal', `${deal.creator} × ${deal.brand}`, result.data[0].id, { creator: deal.creator, brand: deal.brand, amount: deal.amount })
  return result
}
export async function updateDeal(id, updates) {
  const result = await supabase.from('deals').update(updates).eq('id', id).select()
  let d = result.data?.[0]
  // If the update didn't return data (e.g. RLS), fetch the deal separately for the label
  if (!d) {
    const { data } = await supabase.from('deals').select('creator, brand').eq('id', id).single()
    d = data
  }
  const label = d ? `${d.creator} × ${d.brand}` : id
  const changedFields = Object.keys(updates)
  // No row back means the write was filtered (RLS) or the id does not exist.
  // Logging it anyway claimed an edit that never landed.
  if (!result.error && result.data?.length) {
    _log('update', 'deal', label, id, { fields: changedFields, ...updates })
  }
  return result
}
export async function deleteDeal(id) {
  // Fetch deal name before deleting so the log shows a readable label
  const { data } = await supabase.from('deals').select('creator, brand').eq('id', id).single()
  const label = data ? `${data.creator} × ${data.brand}` : null
  return _loggedWrite(
    () => supabase.from('deals').delete().eq('id', id).select('id'),
    () => _log('delete', 'deal', label, id),
  )
}

// --- PROFILES ------------------------------------------------------------------
export async function fetchUserProfile(userId) {
  return supabase.from('profiles').select('*').eq('id', userId).single()
}

export async function fetchAllProfiles() {
  return supabase.from('profiles').select('*').order('created_at')
}
// Canonical team list: every user that can appear as a deal agent / CRM tab.
// Excludes role='creator' (those are talent), role='viewer'.
export async function fetchTeam() {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, agent_name, role, monthly_target')
    .in('role', ['admin', 'agent', 'sales'])
    .order('agent_name')
  if (error) return { data: [], error }
  const seen = new Set()
  const team = (data || []).filter(p => {
    if (!p.agent_name || seen.has(p.agent_name)) return false
    seen.add(p.agent_name); return true
  })
  return { data: team, error: null }
}
// Shared signup helper — calls Edge Function with service role key to create auth user
// This bypasses the "signups not allowed" restriction since it uses admin.createUser server-side
async function _signUpUser(email, password) {
  const session = await getSession()
  const token = session?.access_token
  if (!token) return { id: null, error: { message: 'You must be logged in to create users' } }

  const res = await fetch(`${supabaseUrl}/functions/v1/create-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'apikey': supabaseAnonKey
    },
    body: JSON.stringify({ email, password })
  })
  const data = await res.json()
  if (!res.ok || data.error) {
    return { id: null, error: { message: data.error || 'User creation failed' } }
  }
  return { id: data.id, org_id: data.org_id, error: null }
}

export async function createAgentProfile(email, password, fullName, agentName, role) {
  const { id, org_id, error } = await _signUpUser(email, password)
  if (error) return { data: null, error }
  const result = await supabase.from('profiles').insert([{
    id, full_name: fullName, agent_name: agentName, role, org_id
  }]).select()
  if (result.error) {
    // The login exists at this point but has no profile, so it can't use the
    // app and the email now reads as taken. Say so instead of a bare DB error.
    return { data: null, error: { message: `The login for ${email} was created, but its team profile could not be saved (${result.error.message}). Contact support before retrying with this email.` } }
  }
  _log('create', 'settings', `New agent: ${fullName} (${role})`, id, { role, agent_name: agentName })
  return result
}

// --- REGISTRATION (new org signup) -----------------------------------------------
export async function registerOrg(orgName, fullName, email, password, invitationCode) {
  const res = await fetch(`${supabaseUrl}/functions/v1/register-org`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'apikey': supabaseAnonKey },
    body: JSON.stringify({ orgName, fullName, email, password, invitationCode })
  })
  const data = await res.json()
  if (!res.ok || data.error) return { success: false, error: data.error || 'Registration failed' }
  return { success: true, ...data }
}

// --- COMMUNICATIONS (logged touchpoints against a lead or a brand) -------------
export async function fetchCommunications(leadId) {
  if (!leadId) return { data: [], error: null }
  return supabase
    .from('communications')
    .select('id, user_name, channel, direction, summary, contact_name, occurred_at, next_follow_up')
    .eq('lead_id', leadId)
    .order('occurred_at', { ascending: false })
}

// Inserting also moves the lead's last_contacted / next_follow_up, via trigger.
export async function createCommunication(comm) {
  const result = await supabase.from('communications').insert([comm]).select()
  if (result.data?.[0]) {
    _log('create', 'communication', `${comm.channel} — ${comm.brand || 'lead'}`, result.data[0].id, {
      channel: comm.channel, direction: comm.direction,
    })
  }
  return result
}

export async function deleteCommunication(id) {
  return _loggedWrite(
    () => supabase.from('communications').delete().eq('id', id).select('id'),
    () => _log('delete', 'communication', null, id),
  )
}

// --- CONTRACT PARSING (Claude reads a PDF/image and returns deal fields) --------
// Returns { fields, error }. `fields` may include a `warnings` array the caller
// should surface — the human always reviews before the deal is saved.
export async function parseContract(file) {
  try {
    // 25MB is Anthropic's document ceiling; stay well under it.
    if (file.size > 20 * 1024 * 1024) {
      return { fields: null, error: 'File is too large (max 20MB). Try compressing the PDF.' }
    }
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result).split(',')[1])
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
    const { data, error } = await supabase.functions.invoke('parse-contract', {
      body: { fileBase64: base64, mediaType: file.type, fileName: file.name },
    })
    if (error) return { fields: null, error: error.message || 'Could not read the contract' }
    if (data?.error) return { fields: null, error: data.error }
    return { fields: data?.fields || null, error: null }
  } catch (e) {
    return { fields: null, error: e.message || 'Could not read the contract' }
  }
}

// --- COLLECTION EMAIL DRAFT (Claude-powered payment follow-up) ------------------
export async function draftCollectionEmail(payload) {
  try {
    const { data, error } = await supabase.functions.invoke('draft-collection-email', { body: payload })
    if (error) return { subject: '', body: '', error: error.message || 'Failed' }
    if (data?.error) return { subject: '', body: '', error: data.error }
    return { subject: data?.subject || '', body: data?.body || '', error: null }
  } catch (e) {
    return { subject: '', body: '', error: e.message || 'Network error' }
  }
}

// --- CREATOR AI INSIGHTS (Claude-powered strategic recommendations) ----------------
export async function getCreatorAIInsights(payload) {
  try {
    const { data, error } = await supabase.functions.invoke('creator-ai-insights', {
      body: payload
    })
    if (error) return { recommendations: [], error: error.message || 'Failed to fetch AI insights' }
    if (data?.error) return { recommendations: [], error: data.error }
    return { recommendations: data?.recommendations || [], error: null }
  } catch (e) {
    return { recommendations: [], error: e.message || 'Network error' }
  }
}

// --- APIFY PROXY (server-side, token never reaches browser) ----------------------
export async function apifyProxy(payload) {
  const session = await getSession()
  const token = session?.access_token
  if (!token) return null
  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/apify-proxy`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey
      },
      body: JSON.stringify(payload)
    })
    return await res.json()
  } catch (e) {
    console.error('Apify proxy error:', e)
    return null
  }
}
export async function deleteAgentProfile(id) {
  const { data } = await supabase.from('profiles').select('full_name').eq('id', id).single()
  const name = data?.full_name || 'Unknown'
  return _loggedWrite(
    () => supabase.from('profiles').delete().eq('id', id).select('id'),
    () => _log('delete', 'settings', `Removed agent: ${name}`, id),
  )
}

// --- FILES ---------------------------------------------------------------------
export async function uploadFile(bucket, path, file) {
  const { error } = await supabase.storage.from(bucket).upload(path, file, { upsert: true })
  if (error) return { url: null, error }
  // Use signed URLs (private buckets) — 1 hour expiry
  const { data, error: signError } = await supabase.storage.from(bucket).createSignedUrl(path, 3600)
  if (signError) return { url: null, error: signError }
  return { url: data.signedUrl, error: null }
}
export async function getFileUrl(bucket, path) {
  if (!path) return null
  // Signed URL with 1 hour expiry for private buckets
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600)
  if (error) return null
  return data.signedUrl
}

// Agency-wide recent wins for the creator dashboard: creator, brand, date only.
// Creator logins cannot read other creators' deals; this function is the one
// window they have onto the rest of the agency.
export async function fetchAgencyRecentWins(limit = 8) {
  return supabase.rpc('agency_recent_wins', { p_limit: limit })
}

// Activity feed -- last 10 new deals
export async function fetchActivity() {
  return supabase.from('deals').select('id,creator,brand,agent,created_at').order('created_at', { ascending: false }).limit(10)
}

// Bulk insert for Excel import
export async function bulkInsertDeals(deals) {
  const result = await supabase.from('deals').insert(deals).select()
  if (result.data?.length) _log('create', 'deal', `Bulk import (${result.data.length} deals)`, null, { count: result.data.length })
  return result
}

// --- CREATORS ----------------------------------------------------------------
export async function fetchCreators() {
  // Embeds the linked emane.agency profile (slug) via website_creators.crm_creator_id FK
  return fetchAllPages(() =>
    supabase.from('creators')
      .select('*, website_creators(slug, published)', { count: 'exact' })
      .order('name'))
}
export async function updateCreator(id, updates) {
  const result = await supabase.from('creators').update(updates).eq('id', id).select()
  const name = result.data?.[0]?.name || id
  if (!result.error && result.data?.length) _log('update', 'creator', name, id, updates)
  return result
}
export async function createCreator(name, extras = {}, auth = null) {
  // Get current user's org_id
  const session = await getSession()
  const uid = session?.user?.id
  let orgId = null
  if (uid) {
    const { data: p } = await supabase.from('profiles').select('org_id').eq('id', uid).single()
    orgId = p?.org_id
  }

  // A creator who already has a card only needs a login. Creating the card
  // again would fail on the unique name, after the login had already been
  // made, which is how a login used to end up half-created.
  const { data: existing } = await supabase.from('creators').select('*').eq('name', name).limit(1)
  const card = existing?.[0] || null
  if (card && !(auth?.email && auth?.password)) {
    return { data: null, error: { message: `${name} already has a creator card.` } }
  }

  // If email + password provided, create a login account for this creator.
  // agent_name must match the creator name on their deals exactly: that is
  // what limits a creator login to their own deals.
  if (auth?.email && auth?.password) {
    const { id, org_id, error } = await _signUpUser(auth.email, auth.password)
    if (error) return { data: null, error }
    const profile = await supabase.from('profiles').insert([{
      id, full_name: name, agent_name: name, role: 'creator', org_id: org_id || orgId
    }]).select('id')
    if (profile.error || !profile.data?.length) {
      return { data: null, error: { message: `The login for ${auth.email} was created, but its creator profile could not be saved (${profile.error?.message || 'permission denied'}). Contact support before retrying with this email.` } }
    }
    _log('create', 'creator', `Login for ${name}`, card?.id || null, { email: auth.email })
  }

  if (card) return { data: [card], error: null, loginOnly: true }

  const result = await supabase.from('creators').insert([{ name, ...extras, org_id: orgId }]).select()
  if (!result.error && result.data?.length) {
    _log('create', 'creator', name, result.data[0].id, { name, ...extras })
  }
  return result
}
export async function deleteCreator(id) {
  const { data } = await supabase.from('creators').select('name').eq('id', id).single()
  const name = data?.name || 'Unknown'
  return _loggedWrite(
    () => supabase.from('creators').delete().eq('id', id).select('id'),
    () => _log('delete', 'creator', name, id),
  )
}

// --- BRAND NOTES (CRM) -------------------------------------------------------
export async function fetchBrandNotes(brand) {
  return supabase.from('brand_notes').select('*').eq('brand', brand).order('created_at', { ascending: false })
}
export async function fetchAllBrandNotes() {
  return supabase.from('brand_notes').select('*').order('created_at', { ascending: false })
}
export async function addBrandNote(brand, note, createdBy) {
  const result = await supabase.from('brand_notes').insert([{ brand, note, created_by: createdBy }]).select()
  if (!result.error && result.data?.length) {
    _log('create', 'brand_note', brand, result.data[0].id, { note: note.substring(0, 100) })
  }
  return result
}
export async function deleteBrandNote(id) {
  return _loggedWrite(
    () => supabase.from('brand_notes').delete().eq('id', id).select('id'),
    () => _log('delete', 'brand_note', null, id),
  )
}

// --- LEADS (Personal CRM) ----------------------------------------------------
export async function fetchLeads() {
  return fetchAllPages(() =>
    supabase.from('leads').select('*', { count: 'exact' }).order('created_at', { ascending: false }))
}
export async function createLead(lead) {
  const result = await supabase.from('leads').insert([lead]).select()
  const d = result.data?.[0]
  if (!result.error && d) {
    _log('create', 'lead', d.brand || lead.brand || 'Lead', d.id, { brand: d.brand, client: d.client, status: d.status || lead.status })
  }
  return result
}
export async function updateLead(id, updates) {
  const result = await supabase.from('leads').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id).select()
  const d = result.data?.[0]
  const label = d ? (d.brand || d.client || 'Lead') : 'Lead'
  if (!result.error && d) _log('update', 'lead', label, id, { fields: Object.keys(updates), ...updates })
  return result
}
export async function deleteLead(id) {
  // Fetch lead name before deleting
  const { data } = await supabase.from('leads').select('brand, client').eq('id', id).single()
  const label = data?.brand || data?.client || 'Lead'
  // Returns the result now: this used to swallow it entirely, so a delete the
  // database refused looked exactly like one that worked.
  return _loggedWrite(
    () => supabase.from('leads').delete().eq('id', id).select('id'),
    () => _log('delete', 'lead', label, id),
  )
}

// --- POST THUMBNAIL (Reel / TikTok link -> cover image) ------------------------
// hq=true asks for the sharper Instagram image (slower, costs an Apify run).
export async function fetchThumbnail(url, hq = false) {
  try {
    const { data, error } = await supabase.functions.invoke('fetch-thumbnail', { body: { url, hq } })
    if (error) return { dataUrl: null, error: error.message || 'Could not read that link' }
    if (data?.error) return { dataUrl: null, error: data.error }
    return {
      dataUrl: data?.dataUrl || null,
      platform: data?.platform,
      width: data?.width,
      height: data?.height,
      canUpgrade: data?.canUpgrade === true,
      error: null,
    }
  } catch (e) {
    return { dataUrl: null, error: e.message || 'Network error' }
  }
}

// --- CAMPAIGN REPORT HISTORY (admin only, enforced by RLS) ---------------------
export async function fetchCampaignReports(limit = 50) {
  return supabase
    .from('campaign_reports')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit)
}

// Stores the generated PDF and records it. Called after the file is built, so
// a storage hiccup can't stop the user getting their download.
export async function saveCampaignReport(blob, meta) {
  const session = await getSession()
  const uid = session?.user?.id
  if (!uid) return { error: 'Not signed in' }

  const { data: profile } = await supabase.from('profiles').select('org_id').eq('id', uid).single()
  const slug = `${meta.creator_name || 'creator'}-${meta.brand_name || 'brand'}`
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60)
  const path = `${uid}/${Date.now()}-${slug}.pdf`

  const { error: upErr } = await supabase.storage
    .from('campaign-reports')
    .upload(path, blob, { contentType: 'application/pdf', upsert: false })
  if (upErr) return { error: upErr.message }

  const { error } = await supabase.from('campaign_reports').insert([{
    ...meta,
    org_id: profile?.org_id,
    user_id: uid,
    pdf_path: path,
    file_size: blob.size,
  }])
  if (error) {
    // Don't leave an orphaned file behind if the row didn't land.
    await supabase.storage.from('campaign-reports').remove([path])
    return { error: error.message }
  }
  return { error: null }
}

export async function getCampaignReportUrl(path) {
  const { data, error } = await supabase.storage
    .from('campaign-reports')
    .createSignedUrl(path, 3600)
  return error ? null : data.signedUrl
}

export async function deleteCampaignReport(id, path) {
  await supabase.storage.from('campaign-reports').remove([path])
  return supabase.from('campaign_reports').delete().eq('id', id)
}

// --- COMMISSION PAYOUTS --------------------------------------------------------
export async function fetchPayouts() {
  return supabase.from('commission_payouts').select('*').order('paid_at', { ascending: false })
}
// items: [{ deal_id, amount }] — deals this paycheck covers. Optional for
// ad-hoc payouts; when provided, each deal is linked so the commission tab can
// show "paid in paycheck X" and prevent double-paying the same deal.
export async function createPayout(payout, items = []) {
  // One RPC = one transaction. This used to be two separate inserts: the
  // paycheck first, then its deal links. When the second call failed the
  // paycheck stayed on the books with no deals attached, the activity log had
  // already recorded a success, and retrying issued a SECOND paycheck for the
  // same money. The deal links now live or die with the payout row, and a
  // unique index on payout_items.deal_id refuses to put a deal on two
  // paychecks — the no-double-pay rule this function always claimed to have.
  const { data, error } = await supabase.rpc('create_payout_with_items', {
    p_payout: {
      agent_name: payout.agent_name,
      amount: payout.amount,
      period: payout.period,
      notes: payout.notes ?? null,
      paid_by: payout.paid_by ?? null,
      paid_at: payout.paid_at ?? null,
      org_id: payout.org_id ?? null,
    },
    p_items: (items || []).map(it => ({ deal_id: it.deal_id, amount: it.amount })),
  })

  if (error) {
    // Nothing was written, so nothing is logged. A duplicate deal is the one
    // failure worth naming precisely — it means this paycheck has already
    // been issued, and the caller should not simply retry.
    const duplicate = error.code === '23505' || /payout_items_deal_id_unique/.test(error.message || '')
    return {
      data: null,
      error: duplicate
        ? { ...error, message: 'One or more of these deals is already on a paycheck. Delete the existing payout before re-issuing it.' }
        : error,
    }
  }

  const row = Array.isArray(data) ? data[0] : data
  // Logged only now that the transaction has committed.
  _log('create', 'payout', `${payout.agent_name} — ${payout.period}`, row?.id, { amount: payout.amount, deals: items.length })
  return { data: data ? (Array.isArray(data) ? data : [data]) : [], error: null }
}
export async function fetchPayoutItems() {
  return supabase.from('payout_items').select('*')
}
export async function deletePayout(id) {
  return _loggedWrite(
    () => supabase.from('commission_payouts').delete().eq('id', id).select('id'),
    () => _log('delete', 'payout', null, id),
  )
}

// --- NOTIFICATIONS (in-app) -----------------------------------------------------
export async function fetchNotifications(limit = 30) {
  return supabase.from('notifications')
    .select('id, kind, title, body, entity_type, entity_id, actor_name, read_at, created_at')
    .order('created_at', { ascending: false }).limit(limit)
}
export async function markNotificationsRead(ids) {
  return supabase.rpc('notifications_mark_read', { p_ids: ids && ids.length ? ids : null })
}
